import { combineReducers, configureStore } from "@reduxjs/toolkit";
import type { Middleware } from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";
import {
  persistStore,
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from "redux-persist";
import autoMergeLevel2 from "redux-persist/lib/stateReconciler/autoMergeLevel2";
// @ts-ignore
import storage from "redux-persist-indexeddb-storage";
import { rtkQueryErrorLogger } from "./middlewares";
import { currentUserReducer } from "./current-user/current-user.slice";
import { currentUserApi } from "./current-user/current-user.api";
import { appReducer } from "./app/app.slice";
import { searchReducer } from "./search/search.slice";
import { homeReducer } from "./home/home.slice";
import { modeReducer } from "./mode/mode.slice";
import { genreApi } from "./genre/genre.api";
import { modeApi } from "./mode/mode.api";
import { homeApi } from "./home/home.api";
import { suggestionApi } from "./suggestion/suggestion.api";
import { albumApi } from "./album/album.api";
import { playlistApi } from "./playlist/playlist.api";
import { artistApi } from "./artist/artist.api";
import { trackApi } from "./track/track.api";
import { playerReducer } from "./player/player.slice";
import { searchApi } from "./search/search.api";
import { archiveReducer } from "./archive/archive.slice";

const persistingReducer = combineReducers({
  app: appReducer,
  currentUser: currentUserReducer,
  search: searchReducer,
  player: playerReducer,
  home: homeReducer,
  mode: modeReducer,
  archive: archiveReducer,
});

const persistedReducer = persistReducer<ReturnType<typeof persistingReducer>>(
  {
    key: "redux",
    storage: storage("redux"),
    blacklist: ["player", "user"],
    stateReconciler: autoMergeLevel2,
    debug: process.env.NODE_ENV === 'development',
  },
  persistingReducer
);

const rootReducer = combineReducers({
  persistedReducer,
  [currentUserApi.reducerPath]: currentUserApi.reducer,
  [genreApi.reducerPath]: genreApi.reducer,
  [modeApi.reducerPath]: modeApi.reducer,
  [homeApi.reducerPath]: homeApi.reducer,
  [suggestionApi.reducerPath]: suggestionApi.reducer,
  [albumApi.reducerPath]: albumApi.reducer,
  [playlistApi.reducerPath]: playlistApi.reducer,
  [artistApi.reducerPath]: artistApi.reducer,
  [trackApi.reducerPath]: trackApi.reducer,
  [searchApi.reducerPath]: searchApi.reducer,
});

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }).concat([
      rtkQueryErrorLogger,
      currentUserApi.middleware as Middleware,
      genreApi.middleware as Middleware,
      modeApi.middleware as Middleware,
      homeApi.middleware as Middleware,
      suggestionApi.middleware as Middleware,
      albumApi.middleware as Middleware,
      playlistApi.middleware as Middleware,
      artistApi.middleware as Middleware,
      trackApi.middleware as Middleware,
      searchApi.middleware as Middleware,
    ]),
});
setupListeners(store.dispatch);

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof store.getState>;
export type StorageState = ReturnType<typeof persistedReducer>;
export type AppDispatch = typeof store.dispatch;
